import * as React from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/*
 * AOS design-system numeric input — permanent convention: NO native number inputs.
 * - Live locale-aware thousands grouping via Intl (46000 -> 46.000 de/es, 46,000 en)
 * - Caret position preserved while typing
 * - Canonical raw value (digit string, optional "." decimal) reported via onValueChange;
 *   parents keep storing plain strings and parse with parseFloat / parseInt as before.
 * Props: decimals (0 = integers only), grouping (false for years/heights), locale override.
 */

const sepsFor = (locale) => {
  const parts = new Intl.NumberFormat(locale || undefined).formatToParts(11111.1);
  return {
    group: parts.find((p) => p.type === "group")?.value || ",",
    decimal: parts.find((p) => p.type === "decimal")?.value || ".",
  };
};

const toDisplay = (raw, s, grouping) => {
  if (raw === "" || raw == null) return "";
  const [int = "", frac] = String(raw).split(".");
  const grouped = grouping ? int.replace(/\B(?=(\d{3})+(?!\d))/g, s.group) : int;
  return frac === undefined ? grouped : `${grouped}${s.decimal}${frac}`;
};

export const AmountInput = React.forwardRef(
  ({ value, onValueChange, locale: localeProp, decimals = 0, grouping = true, className, ...props }, forwardedRef) => {
    const { i18n } = useTranslation();
    const locale = localeProp || i18n.resolvedLanguage;
    const s = sepsFor(locale);
    const innerRef = React.useRef(null);
    const pendingCaret = React.useRef(null);

    const setRefs = (el) => {
      innerRef.current = el;
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    };

    const handleChange = (e) => {
      const el = e.target;
      const caret = el.selectionStart ?? el.value.length;
      // significant chars (digits + decimal mark) left of the caret survive formatting
      let sig = 0;
      for (let i = 0; i < caret; i++) {
        const c = el.value[i];
        if (/\d/.test(c) || (decimals > 0 && (c === s.decimal || c === "." || c === ","))) sig++;
      }
      let raw = "";
      let hasDec = false;
      for (const c of el.value) {
        if (/\d/.test(c)) raw += c;
        else if (decimals > 0 && !hasDec && (c === s.decimal || c === "." || c === ",")) {
          raw += ".";
          hasDec = true;
        }
      }
      if (hasDec) {
        const [intPart, fracPart] = raw.split(".");
        raw = `${intPart}.${fracPart.slice(0, decimals)}`;
      }
      pendingCaret.current = sig;
      onValueChange(raw);
    };

    React.useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el || pendingCaret.current == null) return;
      const target = pendingCaret.current;
      pendingCaret.current = null;
      let pos = 0;
      if (target > 0) {
        let count = 0;
        pos = el.value.length;
        for (let i = 0; i < el.value.length; i++) {
          const c = el.value[i];
          if (/\d/.test(c) || c === s.decimal) count++;
          if (count >= target) {
            pos = i + 1;
            break;
          }
        }
      }
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        /* input not focusable right now — nothing to restore */
      }
    });

    return (
      <Input
        ref={setRefs}
        type="text"
        inputMode={decimals > 0 ? "decimal" : "numeric"}
        autoComplete="off"
        spellCheck={false}
        value={toDisplay(value, s, grouping)}
        onChange={handleChange}
        className={cn("tabular-nums", className)}
        {...props}
      />
    );
  }
);
AmountInput.displayName = "AmountInput";
